/**
 * Inline Voice Recording Component
 * Inline recording UI (no modal) similar to Viber/Discord
 */

import { useEffect, useRef } from 'react';
import { useVoiceRecorder } from '@/hooks/useVoiceRecorder';

interface VoiceRecordingInlineProps {
  onCancel: () => void;
  onSend: (audioFile: File) => void;
  preAcquiredStream?: MediaStream;
}

const formatDuration = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const LEVEL_BAR_COUNT = 12;

const AudioLevelBars = ({ level }: { level: number }) => {
  return (
    <div className="flex items-center gap-[2px] h-5">
      {Array.from({ length: LEVEL_BAR_COUNT }, (_, i) => {
        const threshold = (i + 1) / LEVEL_BAR_COUNT;
        const active = level >= threshold * 0.7;
        const baseHeight = 4 + Math.sin((i / LEVEL_BAR_COUNT) * Math.PI) * 12;
        const height = active ? baseHeight + level * 8 : baseHeight;
        return (
          <div
            key={i}
            className={`w-[3px] rounded-full transition-all duration-75 ${
              active ? 'bg-red-500' : 'bg-dark-text-muted/30'
            }`}
            style={{ height: `${Math.max(3, height)}px` }}
          />
        );
      })}
    </div>
  );
};

export const VoiceRecordingInline = ({ onCancel, onSend, preAcquiredStream }: VoiceRecordingInlineProps) => {
  const mountedRef = useRef(false);
  const {
    state,
    duration,
    audioLevel,
    error,
    audioBlob,
    audioUrl,
    startRecording,
    stopRecording,
    cancelRecording,
    resetRecording,
  } = useVoiceRecorder();

  // Auto-start recording on mount — guarded against StrictMode double-mount
  useEffect(() => {
    if (mountedRef.current) return;
    mountedRef.current = true;
    startRecording(preAcquiredStream);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCancel = () => {
    cancelRecording();
    onCancel();
  };

  const handleSend = () => {
    if (!audioBlob) return;

    const extension = audioBlob.type.includes('webm') ? 'webm' :
                      audioBlob.type.includes('ogg') ? 'ogg' :
                      audioBlob.type.includes('mp4') ? 'm4a' : 'webm';

    const fileName = `voice-message-${Date.now()}.${extension}`;
    const audioFile = new File([audioBlob], fileName, { type: audioBlob.type });

    onSend(audioFile);
    resetRecording();
  };

  const isRecording = state === 'recording';
  const isStopped = state === 'stopped';

  return (
    <div className="flex items-center gap-2 w-full bg-red-500/10 border border-red-500/30 rounded-xl px-3 py-2">
      {error && (
        <div className="flex-1 text-sm text-red-400">
          {error}
        </div>
      )}

      {!error && (
        <>
          {isRecording && (
            <>
              {/* Recording indicator + level bars */}
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <div className="w-2.5 h-2.5 bg-red-500 rounded-full animate-pulse shrink-0" />
                <AudioLevelBars level={audioLevel} />
                <span className="text-sm font-mono font-bold text-red-400 tabular-nums shrink-0">
                  {formatDuration(duration)}
                </span>
              </div>

              {/* Stop button */}
              <button
                onClick={stopRecording}
                className="p-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors shrink-0"
                title="Спри"
              >
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                  <rect x="6" y="6" width="12" height="12" rx="1" />
                </svg>
              </button>
            </>
          )}

          {isStopped && audioUrl && (
            <>
              {/* Audio preview */}
              <div className="flex items-center gap-2 flex-1 min-w-0">
                <span className="text-sm text-dark-text-secondary shrink-0">
                  Гласово съобщение ({formatDuration(duration)})
                </span>
                <audio
                  src={audioUrl}
                  controls
                  className="h-8 flex-1 min-w-0"
                  style={{ maxWidth: '200px' }}
                  controlsList="nodownload noplaybackrate"
                />
              </div>

              {/* Send button */}
              <button
                onClick={handleSend}
                className="px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors flex items-center gap-1 shrink-0"
                title="Изпрати"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                  />
                </svg>
                <span className="text-sm">Изпрати</span>
              </button>
            </>
          )}

          {/* Cancel button */}
          <button
            onClick={handleCancel}
            className="p-2 text-dark-text-muted hover:text-dark-text-primary hover:bg-dark-bg-hover rounded-lg transition-colors shrink-0"
            title="Откажи"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </>
      )}
    </div>
  );
};
